import React, { useEffect, useMemo, useState, useCallback } from "react";
import MenuTreeEditor from "../components/MenuTreeEditor";
import SuperAppButton from "../components/SuperAppButton";
import UnifiedSuperAppObjectEditor from "../components/UnifiedSuperAppObjectEditor";
import {
  FaLayerGroup,
  FaList,
  FaToggleOn,
  FaColumns,
  FaDatabase,
  FaCode,
  FaSave,
  FaEye,
  FaPlus,
  FaTrash,
  FaEdit,
} from "react-icons/fa";
import { Modal, Button, Form } from "react-bootstrap";

/**
 * TOP-TO-BOTTOM SUPERAPP CONFIG EDITOR
 *
 * Comprehensive editor for SuperApp configurations, providing visual and JSON editing
 * of all configurable aspects of the SuperApp platform.
 *
 * CONFIGURATION SECTIONS (Top to Bottom):
 * 1. Header - Title, brand logo URL, show likes toggle
 *    - Sets document.title and header display
 *    - Config stored in: config.header
 *
 * 2. Menu Items - Navigation menu with drag-and-drop tree editor
 *    - Add/edit/delete menu items with hierarchical structure
 *    - Support for external URLs and nested children
 *    - Config stored in: config.header.primaryMenu.items (or config.menu/navigation)
 *
 * 3. Feature Flags - Toggle application features
 *    - showLogin: Display login button in header
 *    - showTryForFree: Display trial signup button
 *    - Config stored in: config.features
 *
 * 4. Layout - Default layout configuration
 *    - Visual editor or JSON editor for layout structure
 *    - Defines route, structure (rows/columns/blocks), and block definitions
 *    - Config stored in: config.layout
 *
 * 5. SuperAppObject Editor (Unified) - Create and manage data objects
 *    - Create new objects, edit fields, configure table columns
 *    - Objects stored in: superapp_objects collection AND app_config
 *
 * 6. Raw JSON - Direct JSON editing of entire config
 *    - For advanced users who need full control
 *    - Validates JSON before saving
 *
 * FUNCTION STRINGIFICATION:
 * Since JSON.stringify() silently drops functions, we convert them to strings:
 * - Saving: Functions → { _functionString: "function.toString()" }
 * - Loading: { _functionString: "..." } → eval() to restore executable function
 * - Used for: Table column selectors, validators, event handlers
 * - Note: Uses _functionString (not __function__) because Firestore reserves __ prefix
 *
 * HEADER RENDERING:
 * The header is rendered in App.js based on this config:
 * 1. App.js loads config using useSuperAppConfigWithState() hook
 * 2. Extracts menu items from header.primaryMenu.items (with fallbacks)
 * 3. Passes config to <Header> component which renders menu and brand
 * 4. Listens to 'superapp-config-updated' event for real-time updates
 *
 * ACCESS CONTROL:
 * - Route: /menu-editor?appId=your-app-id
 * - Requires: User must be logged in (checked in App.js)
 * - System Admin: Some features may require system admin role
 * - Dev Mode: Development environments may have relaxed access
 *
 * CONFIG STORAGE:
 * Configs are stored in Firestore collection: superapp_configs
 * Document ID: appId (e.g., "Ben-test")
 * Structure: { config: { header: {...}, menu: [...], ... } }
 *
 * @see {@link ../docs/MENU_EDITOR.md} - Complete Menu Editor documentation
 * @see {@link ../docs/APP_ARCHITECTURE.md} - How headers are rendered in App.js
 * @see {@link ../docs/PAGE_EDITOR.md} - Creating pages with layouts
 * @see {@link ../docs/SUPERAPP_OBJECTS.md} - SuperAppObject creation guide
 *
 * External Resources:
 * - Firestore: https://firebase.google.com/docs/firestore
 * - React State: https://react.dev/learn/managing-state
 * - JSON Schema: https://json-schema.org/
 */

// Helper to always hit same-origin API (avoids https/http mismatch issues)
const api = (path, options = {}) =>
  fetch(`/api${path}`, {
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    credentials: "same-origin",
    ...options,
  });

// Generate unique IDs for new menu items
const generateId = () => Math.random().toString(36).slice(2, 10);

const LAYOUT_OPTIONS = [
  {
    value: "single",
    label: "Single Column",
    structure: { type: "row", children: [{ type: "column", children: [] }] },
  },
  {
    value: "two",
    label: "Two Columns",
    structure: {
      type: "row",
      children: [
        { type: "column", children: [] },
        { type: "column", children: [] },
      ],
    },
  },
  {
    value: "three",
    label: "Three Columns",
    structure: {
      type: "row",
      children: [
        { type: "column", children: [] },
        { type: "column", children: [] },
        { type: "column", children: [] },
      ],
    },
  },
  {
    value: "four",
    label: "Four Columns / Grid",
    structure: {
      type: "row",
      children: [
        { type: "column", children: [] },
        { type: "column", children: [] },
        { type: "column", children: [] },
        { type: "column", children: [] },
      ],
    },
  },
  // Vik (Jan 9, 2026) : Added Tabs option to support tabbed layouts in visual editor
  {
    value: "tabs",
    label: "Tabs",
    structure: {
      type: "tabs",
      tabs: [
        { id: "tab1", label: "Tab 1", block: "" },
        { id: "tab2", label: "Tab 2", block: "" },
      ],
    },
  },
];

// Vik (Jan 9, 2026) : Defined block types for visual editor to standardize block creation
const BLOCK_TYPES = [
  {
    type: "text",
    label: "Text Block",
    icon: "📝",
    defaultData: {
      type: "text",
      title: "New Text Block",
      value: "Enter your text here",
    },
  },
  {
    type: "html",
    label: "HTML Block",
    icon: "🔧",
    defaultData: {
      type: "html",
      title: "New HTML Block",
      html: "<p>Enter your HTML here</p>",
    },
  },
  {
    type: "markdown",
    label: "Markdown Block",
    icon: "📄",
    defaultData: {
      type: "markdown",
      title: "New Markdown Block",
      text: "Enter your markdown here",
    },
  },
  {
    type: "superappobject",
    label: "SuperAppObject",
    icon: "📊",
    defaultData: {
      type: "superappobject",
      configObj: "",
      displayState: "TABLE",
    },
  },
];

/**
 * Main editor component - renders all config sections top to bottom
 */
export default function MenuEditor() {
  const [appId, setAppId] = useState(
    new URLSearchParams(window.location.search).get("appId") || "vik-app-v1"
  );
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    header: true,
    headerJson: false,
    menu: true,
    layout: false,
    features: false,
    configobj: false,
    fields: false,
    raw: false,
  });

  // Header fields
  const [title, setTitle] = useState("");
  const [showLikes, setShowLikes] = useState(true);
  const [brandLogo, setBrandLogo] = useState("");

  // New menu item fields
  const [mLabel, setMLabel] = useState("");
  const [mPathOrUrl, setMPathOrUrl] = useState("");
  const [mOrder, setMOrder] = useState("0");
  const [mVisible, setMVisible] = useState(true);
  const [mParentKey, setMParentKey] = useState("");

  // Features
  const [showLogin, setShowLogin] = useState(true);
  const [showTryForFree, setShowTryForFree] = useState(true);

  // Editable JSON sections
  const [layoutJson, setLayoutJson] = useState("");
  const [rawJson, setRawJson] = useState("");
  const [jsonErrors, setJsonErrors] = useState({ layout: "", raw: "" });

  // Editor mode toggles (visual vs JSON)
  // Editor mode toggles (visual vs JSON)
  const [layoutEditorMode, setLayoutEditorMode] = useState("json"); // "visual" or "json"
  // Vik (Jan 9, 2026) : Added editingTarget state to distinguish between default layout and page-specific layouts
  const [editingTarget, setEditingTarget] = useState("default"); // "default" or page key

  // Vik (Jan 9, 2026) : Centralized layout change handler to update cfg state for both default and page layouts
  const handleLayoutChange = (target, newLayout) => {
    if (target === "default") {
      setLayoutJson(JSON.stringify(newLayout, null, 2));
    } else {
      // Update cfg directly for pages
      const next = { ...cfg };
      if (!next.screens) next.screens = {};
      if (!next.screens[target]) next.screens[target] = {};
      next.screens[target].layout = newLayout;
      setCfg(next);
    }
  };

  // ConfigObj Writer state
  const [configObjList, setConfigObjList] = useState([]);
  const [localDemoConfigObjs, setLocalDemoConfigObjs] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("demoConfigObjs") || "{}");
    } catch {
      return {};
    }
  });
  const [selectedSourceConfigObj, setSelectedSourceConfigObj] = useState("");
  const [newConfigObjName, setNewConfigObjName] = useState("");
  const [configObjLoading, setConfigObjLoading] = useState(false);

  // Field Editor state
  const [selectedConfigObjForFields, setSelectedConfigObjForFields] =
    useState("");
  const [configObjFields, setConfigObjFields] = useState([]);
  const [editingFieldIndex, setEditingFieldIndex] = useState(null);
  const [fieldEditorLoading, setFieldEditorLoading] = useState(false);
  const [fieldForm, setFieldForm] = useState({
    name: "",
    display_name: "",
    input_type: "text",
    placeholder: "",
    required: false,
    readonly: false,
    default: "",
    help: "",
    values: "", // comma-separated for dropdown
  });

  // Table/Column Editor state
  const [selectedConfigObjForTable, setSelectedConfigObjForTable] =
    useState("");
  const [tableColumns, setTableColumns] = useState([]);
  const [editingColumnIndex, setEditingColumnIndex] = useState(null);
  const [tableEditorLoading, setTableEditorLoading] = useState(false);
  const [columnForm, setColumnForm] = useState({
    selector: "",
    name: "",
    sortable: false,
    minWidth: "25%",
    sortOrder: "",
    hide: "",
  });

  /**
   * Extract menu items from config, handling multiple possible locations:
   * - header.primaryMenu.items (preferred, new format)
   * - menu (legacy format)
   * - navigation (legacy format)
   *
   * This ensures backward compatibility with old configs.
   */
  const [menuItems, setMenuItems] = useState([]);

  useEffect(() => {
    console.log("Vik (Jan 16, 2026) : MenuEditor useEffect [cfg]", cfg);
    const fromHeader =
      Array.isArray(cfg?.header?.primaryMenu?.items) &&
      cfg.header.primaryMenu.items.length > 0
        ? cfg.header.primaryMenu.items
        : null;
    const legacyMenu =
      Array.isArray(cfg?.menu) && cfg.menu.length > 0 ? cfg.menu : null;
    const legacyNav =
      Array.isArray(cfg?.navigation) && cfg.navigation.length > 0
        ? cfg.navigation
        : null;

    console.log("Vik (Jan 16, 2026) : Menu Sources", {
      fromHeader,
      legacyMenu,
      legacyNav,
    });

    const nextItems = (fromHeader || legacyMenu || legacyNav || [])
      .slice()
      .sort((a, b) => (a.order ?? 9999) - (b.order ?? 9999)); // Vik (Jan 15, 2026) : Sort by order to match Header display

    console.log("Vik (Jan 16, 2026) : Resolved Menu Items", nextItems);
    setMenuItems(nextItems);
  }, [cfg]);

  /**
   * Save config to server.
   *
   * CRITICAL: Must stringify functions before JSON.stringify()
   * JSON.stringify() silently drops functions, so we convert them to
   * { _functionString: "..." } format first.
   *
   * NOTE: We use _functionString instead of __function__ because Firestore
   * reserves field names starting with double underscores (__).
   *
   * @param {object} nextConfig - The config to save (may contain functions)
   */
  const saveConfig = async (nextConfig) => {
    setSaving(true);
    try {
      // Convert functions to { _functionString: "..." } format
      // This must happen BEFORE JSON.stringify() (which would drop functions)
      // Use _functionString instead of __function__ (Firestore reserves __ prefix)
      const stringifyFunctions = (obj) => {
        if (obj === null || obj === undefined) return obj;

        // Skip objects that are already in our stringified format (new format)
        // This prevents double-processing and infinite recursion
        if (
          typeof obj === "object" &&
          obj._functionString &&
          Object.keys(obj).length === 1
        ) {
          return obj; // Already stringified in new format, return as-is
        }

        // Convert old format (__function__) to new format (_functionString)
        // This handles data that was saved before we changed the format
        if (
          typeof obj === "object" &&
          obj.__function__ &&
          Object.keys(obj).length === 1
        ) {
          return { _functionString: obj.__function__ }; // Migrate old format to new format
        }

        if (typeof obj === "function") {
          return { _functionString: obj.toString() };
        }
        if (Array.isArray(obj)) {
          return obj.map(stringifyFunctions);
        }
        if (typeof obj === "object") {
          const result = {};
          for (const key in obj) {
            if (Object.prototype.hasOwnProperty.call(obj, key)) {
              result[key] = stringifyFunctions(obj[key]);
            }
          }
          return result;
        }
        return obj;
      };
      const prepared = stringifyFunctions(nextConfig);
      const configSize = JSON.stringify(prepared).length;
      console.log(`📦 Config size: ${(configSize / 1024).toFixed(2)} KB`);

      // Firestore has a 1MB document limit - warn if approaching it
      if (configSize > 900 * 1024) {
        console.warn(
          `⚠️ Config is ${(configSize / 1024).toFixed(
            2
          )} KB - approaching Firestore 1MB limit!`
        );
      }

      // Send to server
      const res = await api(`/superapp/config/${encodeURIComponent(appId)}`, {
        method: "PUT",
        body: JSON.stringify({ config: prepared }),
      });
      if (!res.ok) {
        // Try to get error message from response
        let errorMessage = `PUT failed: ${res.status}`;
        try {
          const errorData = await res.json();
          errorMessage = errorData?.error || errorData?.message || errorMessage;
          console.error("Server error response:", errorData);
        } catch (e) {
          const errorText = await res.text();
          console.error("Server error (non-JSON):", errorText);
          errorMessage = errorText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const updated = await res.json();
      setCfg(updated?.config || nextConfig);

      // Notify other components that config was updated
      window.dispatchEvent(
        new CustomEvent("superapp-config-updated", { detail: { appId } })
      );
    } finally {
      setSaving(false);
    }
  };

  /**
   * Normalize config to use header.primaryMenu.items format.
   *
   * This ensures we always write to the preferred location, even if
   * the config came in with legacy menu/navigation arrays.
   *
   * @param {object} baseCfg - Config that may have menu in various locations
   * @returns {object} - Config normalized to header.primaryMenu.items format
   */
  const normalizeForHeader = (baseCfg) => {
    const currItems = Array.isArray(baseCfg?.header?.primaryMenu?.items)
      ? baseCfg.header.primaryMenu.items
      : Array.isArray(baseCfg?.menu)
      ? baseCfg.menu
      : Array.isArray(baseCfg?.navigation)
      ? baseCfg.navigation
      : [];
    return {
      ...(baseCfg || {}),
      header: {
        ...(baseCfg?.header || {}),
        primaryMenu: {
          items: currItems,
        },
      },
    };
  };

  const persistLocalDemoConfig = useCallback((nextAppConfig) => {
    setLocalDemoConfigObjs(nextAppConfig);
    try {
      localStorage.setItem("demoConfigObjs", JSON.stringify(nextAppConfig));
    } catch (e) {
      console.warn("Failed to persist local demo ConfigObjs", e);
    }
  }, []);

  /**
   * Restore functions from { _functionString: "..." } format using eval().
   *
   * When we receive configs from the server, functions are stored as strings.
   * This converts them back to executable functions.
   *
   * @param {any} obj - Object with functions as { _functionString: "..." }
   * @returns {any} - Same object with functions restored
   */
  const restoreFunctions = (obj) => {
    if (obj === null || obj === undefined) return obj;
    // Use _functionString instead of __function__ (Firestore reserves __ prefix)
    if (typeof obj === "object" && obj._functionString) {
      try {
        return eval(`(${obj._functionString})`);
      } catch (e) {
        console.warn("Failed to restore function:", e);
        return obj._functionString;
      }
    }
    if (Array.isArray(obj)) {
      return obj.map(restoreFunctions);
    }
    if (typeof obj === "object") {
      const result = {};
      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          result[key] = restoreFunctions(obj[key]);
        }
      }
      return result;
    }
    return obj;
  };

  /**
   * Load config from server when appId changes.
   *
   * FLOW:
   * 1. Fetch config from server
   * 2. Extract config object
   * 3. Restore functions from { _functionString: "..." } format
   * 4. Update all form fields with loaded values
   *
   * Uses cancellation flag to prevent state updates if component unmounts.
   */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await api(`/superapp/config/${encodeURIComponent(appId)}`);
        if (!res.ok) throw new Error(`GET failed: ${res.status}`);
        const json = await res.json();
        if (!cancelled) {
          const rawConfig = json?.config || {};

          // CRITICAL: Restore functions from { _functionString: "..." } format
          // Server sends functions as strings, we convert them back to executable functions
          const config = restoreFunctions(rawConfig);

          // Update state with loaded values
          setCfg(config);
          setTitle(config?.header?.title || "");
          setShowLikes(Boolean(config?.header?.showLikes));
          setBrandLogo(config?.header?.brand?.logoUrl || "");
          setShowLogin(config?.features?.showLogin !== false);
          setShowTryForFree(config?.features?.showTryForFree !== false);
          // Initialize JSON editors
          setLayoutJson(
            config?.layout ? JSON.stringify(config.layout, null, 2) : ""
          );
          setRawJson(JSON.stringify(config, null, 2));
        }
      } catch (e) {
        console.error(e);
      } finally {
        !cancelled && setLoading(false);
      }
    })();
    return () => {
      cancelled = true; // Prevent state updates if component unmounts
    };
  }, [appId]);

  /**
   * Hash-based Section Navigation
   *
   * Handles URL hash fragments (e.g., #section-menu) to:
   * 1. Automatically expand the target section if it's collapsed
   * 2. Scroll to the section when navigating from the "Open Full Editor →" link
   *
   * This allows direct navigation to specific sections of the Menu Editor,
   * providing a better UX when linking from other parts of the app.
   *
   * Example usage: /menu-editor?appId=Ben-test#section-menu
   */
  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      // Extract section ID from hash (remove the # prefix)
      const sectionId = hash.substring(1);

      // Map hash IDs to section names used in expandedSections state
      const sectionMap = {
        "section-header": "header",
        "section-menu": "menu",
        "section-features": "features",
        "section-layout": "layout",
        "section-configobj": "configobj",
        "section-raw": "raw",
      };

      const sectionName = sectionMap[sectionId];
      if (sectionName) {
        // Expand the target section if it's currently collapsed
        setExpandedSections((prev) => {
          if (!prev[sectionName]) {
            // Section needs to be expanded before we can scroll to it
            // Use setTimeout to ensure DOM has updated after state change
            setTimeout(() => {
              const element = document.getElementById(sectionId);
              if (element) {
                element.scrollIntoView({ behavior: "smooth", block: "start" });
              }
            }, 200); // Longer delay to allow expansion animation
            return { ...prev, [sectionName]: true };
          }
          // Section already expanded, scroll immediately
          setTimeout(() => {
            const element = document.getElementById(sectionId);
            if (element) {
              element.scrollIntoView({ behavior: "smooth", block: "start" });
            }
          }, 100);
          return prev;
        });
      }
    }
  }, []); // Run once on mount

  const handleMenuStructureChange = useCallback(
    async (nextMenuItems) => {
      const base = normalizeForHeader(cfg || {});
      const next = {
        ...base,
        header: {
          ...(base.header || {}),
          primaryMenu: { items: nextMenuItems },
        },
      };
      setMenuItems(nextMenuItems);
      setCfg(next);
      await saveConfig(next);
    },
    [cfg]
  );

  const collectParentOptions = useMemo(() => {
    const options = [];
    const walk = (list, depth = 0) => {
      list.forEach((item) => {
        const keyLower = String(item.key || item.label || "").toLowerCase();
        options.push({ key: keyLower, label: item.label || item.key, depth });
        if (Array.isArray(item.children) && item.children.length > 0) {
          walk(item.children, depth + 1);
        }
      });
    };
    walk(menuItems);
    return options;
  }, [menuItems]);

  const saveHeader = async () => {
    const base = cfg || {};
    const next = {
      ...base,
      header: {
        ...(base.header || {}),
        title: title || "Untitled",
        showLikes,
        brand: {
          ...(base.header?.brand || {}),
          logoUrl: brandLogo || undefined,
        },
        primaryMenu: base?.header?.primaryMenu,
      },
    };
    setCfg(next);
    await saveConfig(next);
  };

  const saveFeatures = async () => {
    const base = cfg || {};
    const next = {
      ...base,
      features: {
        ...(base.features || {}),
        showLogin,
        showTryForFree,
      },
    };
    setCfg(next);
    await saveConfig(next);
  };

  const saveLayout = async () => {
    try {
      let next = { ...cfg };

      if (editingTarget === "default") {
        const parsed = JSON.parse(layoutJson);
        next.layout = parsed;
      } else {
        // For pages, cfg is already updated in state via handleLayoutChange
        // We just need to ensure we are saving the current cfg state
        // Vik (Jan 9, 2026) : Page layouts are updated in cfg state via handleLayoutChange, so we just save the current cfg here
      }

      setCfg(next);
      setJsonErrors((prev) => ({ ...prev, layout: "" }));
      await saveConfig(next);
      alert("Layout saved ✅");
    } catch (e) {
      setJsonErrors((prev) => ({
        ...prev,
        layout: `Invalid JSON: ${e.message}`,
      }));
    }
  };

  const saveRawJson = async () => {
    try {
      const parsed = JSON.parse(rawJson);
      // Restore functions if any were in the original config
      const restored = restoreFunctions(parsed);
      setCfg(restored);
      setJsonErrors((prev) => ({ ...prev, raw: "" }));
      await saveConfig(restored);
      alert("Full config saved ✅");
    } catch (e) {
      setJsonErrors((prev) => ({ ...prev, raw: `Invalid JSON: ${e.message}` }));
    }
  };

  const addMenuItem = async () => {
    if (!mLabel.trim()) {
      alert("Please enter a label.");
      return;
    }

    const key = mLabel.trim().toLowerCase().replace(/\s+/g, "-");
    const isUrl = /^https?:\/\//i.test(mPathOrUrl.trim());
    const newItem = {
      id: `${key}-${generateId()}`,
      key,
      label: mLabel.trim(),
      visible: !!mVisible,
      order: Number.isFinite(parseInt(mOrder, 10)) ? parseInt(mOrder, 10) : 0,
      ...(mPathOrUrl.trim()
        ? isUrl
          ? { url: mPathOrUrl.trim(), target: "_blank" }
          : {
              path: mPathOrUrl.trim().startsWith("/")
                ? mPathOrUrl.trim()
                : `/${mPathOrUrl.trim()}`,
            }
        : { path: `/${key}` }),
    };

    const base = normalizeForHeader(cfg || {});
    const current = base.header.primaryMenu.items || [];
    const nextRoot = [...current.map((item) => ({ ...item }))];

    if (mParentKey) {
      const parentKeyLower = String(mParentKey).toLowerCase();
      const injectChild = (list) => {
        for (let i = 0; i < list.length; i++) {
          const item = list[i];
          const itemKeyLower = String(
            item.key || item.label || ""
          ).toLowerCase();
          if (itemKeyLower === parentKeyLower) {
            const existingChildren = Array.isArray(item.children)
              ? item.children.slice()
              : [];
            existingChildren.push(newItem);
            list[i] = { ...item, children: existingChildren };
            return true;
          }
          if (Array.isArray(item.children) && injectChild(item.children))
            return true;
        }
        return false;
      };
      const inserted = injectChild(nextRoot);
      if (!inserted) nextRoot.push(newItem);
    } else {
      nextRoot.push(newItem);
    }

    const next = {
      ...base,
      header: {
        ...(base.header || {}),
        title: title || base?.header?.title || "",
        showLikes,
        primaryMenu: { items: nextRoot },
      },
    };

    setMenuItems(nextRoot);
    setCfg(next);
    await saveConfig(next);

    setMLabel("");
    setMPathOrUrl("");
    setMOrder(String((newItem.order ?? 0) + 1));
    setMVisible(true);
    setMParentKey("");
  };

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));

    // Load configObj list when opening the configobj section
    if (section === "configobj" && !expandedSections.configobj) {
      loadConfigObjList();
    }
    // Load fields when opening the fields section
    if (section === "fields" && !expandedSections.fields) {
      loadConfigObjList();
    }
    // Load table columns when opening the table section
    if (section === "table" && !expandedSections.table) {
      loadConfigObjList();
    }
  };

  /**
   * Load list of available configObjs from the config collection
   */
  const loadConfigObjList = async () => {
    try {
      setConfigObjLoading(true);
      const response = await api("/getObjects/config");
      const configArray = await response.json();

      if (Array.isArray(configArray) && configArray.length > 0) {
        const fullConfig = configArray[0];
        const configObjNames = fullConfig.app_config
          ? Object.keys(fullConfig.app_config)
          : [];
        setConfigObjList(configObjNames);
      } else {
        setConfigObjList(Object.keys(localDemoConfigObjs || {}));
      }
    } catch (e) {
      console.error("Failed to load configObj list:", e);
      setConfigObjList(Object.keys(localDemoConfigObjs || {}));
    } finally {
      setConfigObjLoading(false);
    }
  };

  /**
   * Copy a configObj from the config collection to create a new one
   */
  const copyConfigObj = async () => {
    if (!selectedSourceConfigObj || !newConfigObjName) {
      alert("Please select a source configObj and enter a new name");
      return;
    }

    if (newConfigObjName === selectedSourceConfigObj) {
      alert("New name must be different from source name");
      return;
    }

    try {
      setConfigObjLoading(true);

      // Fetch current config
      const response = await api("/getObjects/config");
      const configArray = await response.json();

      if (!Array.isArray(configArray) || configArray.length === 0) {
        throw new Error("No config found in database");
      }

      const fullConfig = configArray[0];
      if (
        !fullConfig.app_config ||
        !fullConfig.app_config[selectedSourceConfigObj]
      ) {
        throw new Error(
          `Source configObj "${selectedSourceConfigObj}" not found`
        );
      }

      // Deep copy the source configObj
      const sourceConfigObj = JSON.parse(
        JSON.stringify(fullConfig.app_config[selectedSourceConfigObj])
      );

      // Update the name/tableName if present
      if (sourceConfigObj.name) {
        sourceConfigObj.name = newConfigObjName;
      }
      if (sourceConfigObj.tableName) {
        sourceConfigObj.tableName = newConfigObjName.toLowerCase();
      }
      if (sourceConfigObj.collection) {
        sourceConfigObj.collection = newConfigObjName.toLowerCase();
      }

      // Add the new configObj to app_config
      if (!fullConfig.app_config) {
        fullConfig.app_config = {};
      }
      fullConfig.app_config[newConfigObjName] = sourceConfigObj;

      // Save back to database
      const saveResponse = await api("/saveObject/config", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(fullConfig),
      });

      const saveData = await saveResponse.json();
      if (saveData.success) {
        alert(
          `Successfully copied "${selectedSourceConfigObj}" to "${newConfigObjName}"`
        );
        setNewConfigObjName("");
        setSelectedSourceConfigObj("");
        loadConfigObjList(); // Refresh the list
      } else {
        throw new Error(saveData.error || "Failed to save configObj");
      }
    } catch (e) {
      console.error("Failed to copy configObj:", e);
      const localSource = localDemoConfigObjs?.[selectedSourceConfigObj];
      if (localSource) {
        const cloned = JSON.parse(JSON.stringify(localSource));
        if (cloned.name) cloned.name = newConfigObjName;
        if (cloned.tableName) cloned.tableName = newConfigObjName.toLowerCase();
        if (cloned.collection)
          cloned.collection = newConfigObjName.toLowerCase();
        const updatedLocal = {
          ...localDemoConfigObjs,
          [newConfigObjName]: cloned,
        };
        persistLocalDemoConfig(updatedLocal);
        setConfigObjList(Object.keys(updatedLocal));
        alert(
          `Copied "${selectedSourceConfigObj}" to "${newConfigObjName}" locally (demo mode).`
        );
      } else {
        alert(`Error: ${e.message || "Failed to copy configObj"}`);
      }
    } finally {
      setConfigObjLoading(false);
    }
  };

  /**
   * Load fields for a selected configObj
   */
  const loadConfigObjFields = async (configObjName) => {
    if (!configObjName) {
      setConfigObjFields([]);
      return;
    }

    try {
      setFieldEditorLoading(true);
      const response = await api("/getObjects/config");
      const configArray = await response.json();

      if (Array.isArray(configArray) && configArray.length > 0) {
        const fullConfig = configArray[0];
        if (fullConfig.app_config && fullConfig.app_config[configObjName]) {
          const configObj = fullConfig.app_config[configObjName];
          const fields = Array.isArray(configObj.fields)
            ? configObj.fields
            : [];
          setConfigObjFields(fields);
        } else {
          setConfigObjFields(
            localDemoConfigObjs?.[configObjName]?.fields || []
          );
        }
      } else {
        setConfigObjFields(localDemoConfigObjs?.[configObjName]?.fields || []);
      }
    } catch (e) {
      console.error("Failed to load configObj fields:", e);
      setConfigObjFields(localDemoConfigObjs?.[configObjName]?.fields || []);
    } finally {
      setFieldEditorLoading(false);
    }
  };

  /**
   * Save fields back to configObj in database
   */
  const saveConfigObjFields = async () => {
    if (!selectedConfigObjForFields) {
      alert("Please select a configObj first");
      return;
    }

    try {
      setFieldEditorLoading(true);

      // Fetch current config
      const response = await api("/getObjects/config");
      const configArray = await response.json();

      if (!Array.isArray(configArray) || configArray.length === 0) {
        throw new Error("No config found in database");
      }

      const fullConfig = configArray[0];
      if (
        !fullConfig.app_config ||
        !fullConfig.app_config[selectedConfigObjForFields]
      ) {
        throw new Error(`ConfigObj "${selectedConfigObjForFields}" not found`);
      }

      // Update fields
      fullConfig.app_config[selectedConfigObjForFields].fields =
        configObjFields;

      // Save back to database
      const saveResponse = await api("/saveObject/config", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(fullConfig),
      });

      const saveData = await saveResponse.json();
      if (saveData.success) {
        alert(`Successfully saved fields for "${selectedConfigObjForFields}"`);
        setEditingFieldIndex(null);
        resetFieldForm();
      } else {
        throw new Error(saveData.error || "Failed to save fields");
      }
    } catch (e) {
      console.error("Failed to save fields:", e);
      const localConfig = localDemoConfigObjs?.[selectedConfigObjForFields];
      if (localConfig) {
        const updated = {
          ...localDemoConfigObjs,
          [selectedConfigObjForFields]: {
            ...localConfig,
            fields: configObjFields,
          },
        };
        persistLocalDemoConfig(updated);
        alert(
          `Saved fields for "${selectedConfigObjForFields}" locally (demo mode).`
        );
        setEditingFieldIndex(null);
        resetFieldForm();
      } else {
        alert(`Error: ${e.message || "Failed to save fields"}`);
      }
    } finally {
      setFieldEditorLoading(false);
    }
  };

  /**
   * Add a new field
   */
  const addField = () => {
    if (!fieldForm.name || !fieldForm.display_name) {
      alert("Name and Display Name are required");
      return;
    }

    // Check if field name already exists
    if (configObjFields.some((f) => f.name === fieldForm.name)) {
      alert(`Field with name "${fieldForm.name}" already exists`);
      return;
    }

    const newField = {
      name: fieldForm.name,
      display_name: fieldForm.display_name,
      input_type: fieldForm.input_type,
      placeholder: fieldForm.placeholder || "",
      required: fieldForm.required || false,
      readonly: fieldForm.readonly || false,
      default: fieldForm.default || "",
      help: fieldForm.help || "",
    };

    // Add values for dropdown
    if (fieldForm.input_type === "dropdown" && fieldForm.values) {
      newField.values = fieldForm.values
        .split(",")
        .map((v) => v.trim())
        .filter((v) => v);
    }

    setConfigObjFields([...configObjFields, newField]);
    resetFieldForm();
  };

  /**
   * Start editing a field
   */
  const startEditField = (index) => {
    const field = configObjFields[index];
    setFieldForm({
      name: field.name || "",
      display_name: field.display_name || "",
      input_type: field.input_type || "text",
      placeholder: field.placeholder || "",
      required: field.required || false,
      readonly: field.readonly || false,
      default: field.default || "",
      help: field.help || "",
      values: Array.isArray(field.values) ? field.values.join(", ") : "",
    });
    setEditingFieldIndex(index);
  };

  /**
   * Update field being edited
   */
  const updateField = () => {
    if (editingFieldIndex === null) return;
    if (!fieldForm.name || !fieldForm.display_name) {
      alert("Name and Display Name are required");
      return;
    }

    const updatedFields = [...configObjFields];
    const updatedField = {
      ...updatedFields[editingFieldIndex],
      name: fieldForm.name,
      display_name: fieldForm.display_name,
      input_type: fieldForm.input_type,
      placeholder: fieldForm.placeholder || "",
      required: fieldForm.required || false,
      readonly: fieldForm.readonly || false,
      default: fieldForm.default || "",
      help: fieldForm.help || "",
    };

    // Update values for dropdown
    if (fieldForm.input_type === "dropdown" && fieldForm.values) {
      updatedField.values = fieldForm.values
        .split(",")
        .map((v) => v.trim())
        .filter((v) => v);
    } else if (fieldForm.input_type !== "dropdown") {
      delete updatedField.values;
    }

    updatedFields[editingFieldIndex] = updatedField;
    setConfigObjFields(updatedFields);
    setEditingFieldIndex(null);
    resetFieldForm();
  };

  /**
   * Delete a field
   */
  const deleteField = (index) => {
    if (
      !window.confirm(`Delete field "${configObjFields[index].display_name}"?`)
    ) {
      return;
    }
    const updatedFields = configObjFields.filter((_, i) => i !== index);
    setConfigObjFields(updatedFields);
    if (editingFieldIndex === index) {
      setEditingFieldIndex(null);
      resetFieldForm();
    }
  };

  /**
   * Reset field form
   */
  const resetFieldForm = () => {
    setFieldForm({
      name: "",
      display_name: "",
      input_type: "text",
      placeholder: "",
      required: false,
      readonly: false,
      default: "",
      help: "",
      values: "",
    });
  };

  /**
   * Cancel editing
   */
  const cancelEdit = () => {
    setEditingFieldIndex(null);
    resetFieldForm();
  };

  // Load fields when configObj selection changes
  useEffect(() => {
    if (selectedConfigObjForFields) {
      loadConfigObjFields(selectedConfigObjForFields);
    } else {
      setConfigObjFields([]);
    }
  }, [selectedConfigObjForFields]);

  // Load table columns when configObj selection changes
  useEffect(() => {
    if (selectedConfigObjForTable) {
      loadTableColumns(selectedConfigObjForTable);
    } else {
      setTableColumns([]);
    }
  }, [selectedConfigObjForTable]);

  /**
   * Load table columns for a selected configObj
   */
  const loadTableColumns = async (configObjName) => {
    if (!configObjName) {
      setTableColumns([]);
      return;
    }

    try {
      setTableEditorLoading(true);
      const response = await api("/getObjects/config");
      const configArray = await response.json();

      if (Array.isArray(configArray) && configArray.length > 0) {
        const fullConfig = configArray[0];
        if (fullConfig.app_config && fullConfig.app_config[configObjName]) {
          const configObj = fullConfig.app_config[configObjName];
          const columns = Array.isArray(configObj.table) ? configObj.table : [];
          // Convert function selectors to strings for editing
          const columnsForEditing = columns.map((col) => {
            const colCopy = { ...col };
            if (
              colCopy.selector &&
              typeof colCopy.selector === "object" &&
              colCopy.selector._functionString
            ) {
              const match = colCopy.selector._functionString.match(
                /row\[?['"](.*?)['"]\]?/
              );
              colCopy.selector = match ? match[1] : "";
            } else if (typeof colCopy.selector === "function") {
              const match = colCopy.selector
                .toString()
                .match(/row\[?['"](.*?)['"]\]?/);
              colCopy.selector = match ? match[1] : "";
            }
            return colCopy;
          });
          setTableColumns(columnsForEditing);
        } else {
          const fallbackCols =
            localDemoConfigObjs?.[configObjName]?.table?.map((col) => {
              const colCopy = { ...col };
              // Use _functionString instead of __function__ (Firestore reserves __ prefix)
              if (
                colCopy.selector &&
                typeof colCopy.selector === "object" &&
                colCopy.selector._functionString
              ) {
                const match = colCopy.selector._functionString.match(
                  /row\[?['"](.*?)['"]\]?/
                );
                colCopy.selector = match ? match[1] : "";
              } else if (typeof colCopy.selector === "function") {
                const match = colCopy.selector
                  .toString()
                  .match(/row\[?['"](.*?)['"]\]?/);
                colCopy.selector = match ? match[1] : "";
              }
              return colCopy;
            }) || [];
          setTableColumns(fallbackCols);
        }
      } else {
        const fallbackCols =
          localDemoConfigObjs?.[configObjName]?.table?.map((col) => {
            const colCopy = { ...col };
            if (
              colCopy.selector &&
              typeof colCopy.selector === "object" &&
              colCopy.selector._functionString
            ) {
              const match = colCopy.selector._functionString.match(
                /row\[?['"](.*?)['"]\]?/
              );
              colCopy.selector = match ? match[1] : "";
            } else if (typeof colCopy.selector === "function") {
              const match = colCopy.selector
                .toString()
                .match(/row\[?['"](.*?)['"]\]?/);
              colCopy.selector = match ? match[1] : "";
            }
            return colCopy;
          }) || [];
        setTableColumns(fallbackCols);
      }
    } catch (e) {
      console.error("Failed to load table columns:", e);
      const fallbackCols =
        localDemoConfigObjs?.[configObjName]?.table?.map((col) => {
          const colCopy = { ...col };
          // Use _functionString instead of __function__ (Firestore reserves __ prefix)
          if (
            colCopy.selector &&
            typeof colCopy.selector === "object" &&
            colCopy.selector._functionString
          ) {
            const match = colCopy.selector._functionString.match(
              /row\[?['"](.*?)['"]\]?/
            );
            colCopy.selector = match ? match[1] : "";
          } else if (typeof colCopy.selector === "function") {
            const match = colCopy.selector
              .toString()
              .match(/row\[?['"](.*?)['"]\]?/);
            colCopy.selector = match ? match[1] : "";
          }
          return colCopy;
        }) || [];
      setTableColumns(fallbackCols);
    } finally {
      setTableEditorLoading(false);
    }
  };

  /**
   * Save table columns back to configObj in database
   */
  const saveTableColumns = async () => {
    if (!selectedConfigObjForTable) {
      alert("Please select a configObj first");
      return;
    }

    let columnsWithFunctions = [];
    try {
      setTableEditorLoading(true);

      // Fetch current config
      const response = await api("/getObjects/config");
      const configArray = await response.json();

      if (!Array.isArray(configArray) || configArray.length === 0) {
        throw new Error("No config found in database");
      }

      const fullConfig = configArray[0];
      if (
        !fullConfig.app_config ||
        !fullConfig.app_config[selectedConfigObjForTable]
      ) {
        throw new Error(`ConfigObj "${selectedConfigObjForTable}" not found`);
      }

      // Update table columns - convert selector strings to functions
      columnsWithFunctions = tableColumns.map((col) => {
        const colCopy = { ...col };
        // Convert selector string to function if it's a string
        if (typeof colCopy.selector === "string") {
          const fieldName = colCopy.selector.trim();
          // Create function: (row) => row[fieldName]
          colCopy.selector = {
            _functionString: `(row) => row['${fieldName}']`,
          };
        } else if (typeof colCopy.selector === "function") {
          // Already a function, stringify it
          colCopy.selector = { _functionString: colCopy.selector.toString() };
        }
        return colCopy;
      });
      fullConfig.app_config[selectedConfigObjForTable].table =
        columnsWithFunctions;

      // Save back to database
      const saveResponse = await api("/saveObject/config", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(fullConfig),
      });

      const saveData = await saveResponse.json();
      if (saveData.success) {
        alert(
          `Successfully saved table columns for "${selectedConfigObjForTable}"`
        );
        setEditingColumnIndex(null);
        resetColumnForm();
      } else {
        throw new Error(saveData.error || "Failed to save table columns");
      }
    } catch (e) {
      console.error("Failed to save table columns:", e);
      const localConfig = localDemoConfigObjs?.[selectedConfigObjForTable];
      if (localConfig) {
        const fallbackColumns =
          columnsWithFunctions.length > 0
            ? columnsWithFunctions
            : tableColumns.map((col) => {
                const colCopy = { ...col };
                if (typeof colCopy.selector === "string") {
                  const fieldName = colCopy.selector.trim();
                  colCopy.selector = {
                    _functionString: `(row) => row['${fieldName}']`,
                  };
                }
                return colCopy;
              });
        const updated = {
          ...localDemoConfigObjs,
          [selectedConfigObjForTable]: {
            ...localConfig,
            table: fallbackColumns,
          },
        };
        persistLocalDemoConfig(updated);
        alert(
          `Saved table columns for "${selectedConfigObjForTable}" locally (demo mode).`
        );
        setEditingColumnIndex(null);
        resetColumnForm();
      } else {
        alert(`Error: ${e.message || "Failed to save table columns"}`);
      }
    } finally {
      setTableEditorLoading(false);
    }
  };

  /**
   * Add a new table column
   */
  const addColumn = () => {
    if (!columnForm.selector || !columnForm.name) {
      alert("Selector and Name are required");
      return;
    }

    // Convert selector string to function (stored as string for now, will be converted on save)
    const newColumn = {
      selector: columnForm.selector, // Will be converted to function: (row) => row[selector]
      name: columnForm.name,
      sortable: columnForm.sortable || false,
      minWidth: columnForm.minWidth || "25%",
    };

    if (columnForm.sortOrder) {
      newColumn.sortOrder = columnForm.sortOrder;
    }
    if (columnForm.hide) {
      newColumn.hide = columnForm.hide;
    }

    setTableColumns([...tableColumns, newColumn]);
    resetColumnForm();
  };

  /**
   * Start editing a column
   */
  const startEditColumn = (index) => {
    const column = tableColumns[index];
    // Extract selector - if it's a function, we can't edit it easily, so show the field name
    const selectorStr =
      typeof column.selector === "function"
        ? column.selector.toString().match(/row\[?['"](.*?)['"]\]?/)?.[1] || ""
        : column.selector || "";

    setColumnForm({
      selector: selectorStr,
      name: column.name || "",
      sortable: column.sortable || false,
      minWidth: column.minWidth || "25%",
      sortOrder: column.sortOrder || "",
      hide: column.hide || "",
    });
    setEditingColumnIndex(index);
  };

  /**
   * Update column being edited
   */
  const updateColumn = () => {
    if (editingColumnIndex === null) return;
    if (!columnForm.selector || !columnForm.name) {
      alert("Selector and Name are required");
      return;
    }

    const updatedColumns = [...tableColumns];
    const updatedColumn = {
      ...updatedColumns[editingColumnIndex],
      selector: columnForm.selector, // Will be converted to function on render
      name: columnForm.name,
      sortable: columnForm.sortable || false,
      minWidth: columnForm.minWidth || "25%",
    };

    if (columnForm.sortOrder) {
      updatedColumn.sortOrder = columnForm.sortOrder;
    } else {
      delete updatedColumn.sortOrder;
    }
    if (columnForm.hide) {
      updatedColumn.hide = columnForm.hide;
    } else {
      delete updatedColumn.hide;
    }

    updatedColumns[editingColumnIndex] = updatedColumn;
    setTableColumns(updatedColumns);
    setEditingColumnIndex(null);
    resetColumnForm();
  };

  /**
   * Delete a column
   */
  const deleteColumn = (index) => {
    if (!window.confirm(`Delete column "${tableColumns[index].name}"?`)) {
      return;
    }
    const updatedColumns = tableColumns.filter((_, i) => i !== index);
    setTableColumns(updatedColumns);
    if (editingColumnIndex === index) {
      setEditingColumnIndex(null);
      resetColumnForm();
    }
  };

  /**
   * Reset column form
   */
  const resetColumnForm = () => {
    setColumnForm({
      selector: "",
      name: "",
      sortable: false,
      minWidth: "25%",
      sortOrder: "",
      hide: "",
    });
  };

  /**
   * Cancel editing column
   */
  const cancelEditColumn = () => {
    setEditingColumnIndex(null);
    resetColumnForm();
  };

  /**
   * Build demo ConfigObj definitions used for showcasing ConfigObj Writer / Field / Table editors.
   */
  const buildDemoAppConfig = () => ({
    Users: {
      name: "Users",
      tableName: "users",
      collection: "users",
      displayType: "TABLE",
      showLikes: false,
      buttonName: "Save User",
      security: {
        view: "AllUsers",
        create: "AllUsers",
        edit: "AllUsers",
        delete: "AdminsOnly",
      },
      fields: [
        {
          name: "userId",
          display_name: "User ID",
          input_type: "text",
          required: true,
          help: "Internal identifier or email for the user",
          colspan: 1,
        },
        {
          name: "email",
          display_name: "Email",
          input_type: "email",
          required: true,
          colspan: 1,
        },
        {
          name: "planName",
          display_name: "Plan",
          input_type: "dropdown",
          values: "Starter,Pro,Enterprise",
          default: "Starter",
          colspan: 1,
        },
        {
          name: "roles",
          display_name: "Roles",
          input_type: "text",
          help: "Comma-separated roles (admin, editor, viewer)",
          colspan: 1,
        },
        {
          name: "status",
          display_name: "Status",
          input_type: "dropdown",
          values: "Active,Pending,Inactive",
          default: "Active",
          colspan: 1,
        },
        {
          name: "notes",
          display_name: "Notes",
          input_type: "textarea",
          colspan: 2,
        },
      ],
      table: [
        {
          name: "User",
          selector: {
            _functionString: "(row) => row.userId || row.email || ''",
          },
          sortable: true,
          minWidth: "180px",
        },
        {
          name: "Plan",
          selector: { _functionString: "(row) => row.planName || ''" },
          sortable: true,
          minWidth: "140px",
        },
        {
          name: "Roles",
          selector: { _functionString: "(row) => row.roles || ''" },
          minWidth: "200px",
        },
        {
          name: "Status",
          selector: { _functionString: "(row) => row.status || ''" },
          sortable: true,
          minWidth: "120px",
        },
      ],
      filterField: "userId",
      exportFields: ["userId", "email", "planName", "roles", "status"],
      otherTableOptions: { paginationPerPage: 10 },
    },
    TrialRequests: {
      name: "Trial Requests",
      tableName: "trial_requests",
      collection: "trialRequests",
      displayType: "TABLE",
      buttonName: "Log Request",
      security: {
        view: "AllUsers",
        create: "AllUsers",
        edit: "AdminsOnly",
        delete: "AdminsOnly",
      },
      fields: [
        {
          name: "companyName",
          display_name: "Company Name",
          input_type: "text",
          required: true,
          colspan: 2,
        },
        {
          name: "contactName",
          display_name: "Contact Name",
          input_type: "text",
          required: true,
          colspan: 1,
        },
        {
          name: "contactEmail",
          display_name: "Contact Email",
          input_type: "email",
          required: true,
          colspan: 1,
        },
        {
          name: "requestedPlan",
          display_name: "Requested Plan",
          input_type: "dropdown",
          values: "Starter,Pro,Enterprise,Custom",
          default: "Pro",
          colspan: 1,
        },
        {
          name: "dealSize",
          display_name: "Estimated Deal Size",
          input_type: "number",
          help: "Projected annual contract value",
          colspan: 1,
        },
        {
          name: "notes",
          display_name: "Notes",
          input_type: "textarea",
          colspan: 2,
        },
      ],
      table: [
        {
          name: "Company",
          selector: { _functionString: "(row) => row.companyName || ''" },
          sortable: true,
          minWidth: "200px",
        },
        {
          name: "Contact",
          selector: { __function__: "(row) => row.contactName || ''" },
          minWidth: "180px",
        },
        {
          name: "Email",
          selector: { __function__: "(row) => row.contactEmail || ''" },
          minWidth: "220px",
        },
        {
          name: "Plan",
          selector: { __function__: "(row) => row.requestedPlan || ''" },
          minWidth: "140px",
        },
        {
          name: "Deal Size",
          selector: {
            _functionString:
              "(row) => row.dealSize ? `$${row.dealSize}` : 'TBD'",
          },
          minWidth: "140px",
        },
      ],
      filterField: "companyName",
      otherTableOptions: { paginationPerPage: 5 },
    },
  });

  /**
   * Seed demo ConfigObjs into superapp_configs (not config collection)
   * This keeps everything in one place and doesn't touch the boss's config collection
   */
  const seedDemoConfigCollection = async (appConfig) => {
    try {
      setConfigObjLoading(true);
      // ConfigObjs will be stored in superapp_configs, so we don't need to fetch from config collection
      // Just return success - the app_config will be included in the main SuperApp config save
      setConfigObjList(Object.keys(appConfig));
      persistLocalDemoConfig({});
      return true;
    } catch (err) {
      console.warn("Error seeding demo ConfigObjs:", err);
      persistLocalDemoConfig(appConfig);
      setConfigObjList(Object.keys(appConfig));
      return false;
    } finally {
      setConfigObjLoading(false);
    }
  };

  /**
   * Load a demo/template SuperApp config to showcase all features
   * This creates a complete example config with header, menu, layout, and screens
   */
  const loadDemoConfig = async () => {
    if (
      !window.confirm(
        "This will replace your current config with a demo template. Continue?"
      )
    ) {
      return;
    }

    const demoAppConfig = buildDemoAppConfig();
    const demoConfig = {
      header: {
        title: "SuperApp Demo",
        brand: {
          logoUrl: "",
        },
        showLikes: true,
        primaryMenu: {
          items: [
            {
              key: "home",
              label: "Home",
              path: "/home",
              visible: true,
              order: 0,
            },
            {
              key: "products",
              label: "Products",
              path: "/products",
              visible: true,
              order: 1,
            },
            {
              key: "docs",
              label: "Documentation",
              path: "/docs",
              visible: true,
              order: 2,
              children: [
                {
                  key: "guides",
                  label: "Guides",
                  path: "/docs/guides",
                  visible: true,
                  order: 0,
                },
                {
                  key: "api",
                  label: "API Reference",
                  path: "/docs/api",
                  visible: true,
                  order: 1,
                },
              ],
            },
            {
              key: "about",
              label: "About",
              path: "/about",
              visible: true,
              order: 3,
            },
          ],
        },
      },
      features: {
        showLogin: true,
        showTryForFree: true,
      },
      layout: {
        route: "/",
        structure: {
          type: "row",
          sectionProps: {
            style: {
              display: "flex",
              flexDirection: "column",
              gap: "24px",
              padding: "24px",
            },
          },
          children: [
            {
              type: "block",
              block: "hero",
              id: "hero-block",
            },
            {
              type: "row",
              sectionProps: {
                style: {
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "24px",
                },
              },
              children: [
                {
                  type: "block",
                  block: "features",
                  id: "features-block",
                },
                {
                  type: "block",
                  block: "cta",
                  id: "cta-block",
                },
              ],
            },
            {
              type: "block",
              block: "table-demo",
              id: "table-demo-block",
            },
            {
              type: "block",
              block: "table-demo-object",
              id: "table-demo-object-block",
            },
          ],
        },
        blocks: {
          hero: {
            type: "hero",
            title: "Welcome to SuperApp",
            subtitle: "A powerful, configurable application platform",
            cta: {
              label: "Get Started",
              href: "/signup",
              target: "_self",
            },
          },
          features: {
            type: "cards",
            title: "Key Features",
            items: [
              {
                title: "Dynamic Layouts",
                text: "Configure your page layouts without code",
                link: {
                  label: "Learn More",
                  href: "/docs/layouts",
                  target: "_self",
                },
              },
              {
                title: "SuperAppObjects",
                text: "Render data tables and forms dynamically",
                link: {
                  label: "Explore",
                  href: "/products",
                  target: "_self",
                },
              },
              {
                title: "Low-Code Pages",
                text: "Create pages with JSON configuration",
                link: {
                  label: "Try It",
                  href: "/docs",
                  target: "_self",
                },
              },
            ],
          },
          cta: {
            type: "cta",
            title: "Ready to Get Started?",
            text: "Sign up today and start building your SuperApp",
            button: {
              label: "Sign Up Now",
              href: "/signup",
              target: "_self",
            },
          },
          "table-demo": {
            type: "text",
            title: "Datagrid/Table Configuration Demo",
            value:
              "Below is a live example of a SuperAppObject table. All columns, sorting, and display options are configured in the database using the Table/Column Editor (section 7) - no code changes needed!",
          },
          "table-demo-object": {
            type: "superappobject",
            configObj: "Users",
            displayState: "TABLE",
          },
        },
      },
      screens: {
        home: {
          type: "layout",
          structure: {
            type: "row",
            sectionProps: {
              style: {
                display: "flex",
                flexDirection: "column",
                gap: "24px",
                padding: "24px",
              },
            },
            children: [
              {
                type: "block",
                block: "home-welcome",
                id: "home-welcome-block",
              },
              {
                type: "block",
                block: "home-users-table",
                id: "home-users-table-block",
              },
            ],
          },
          blocks: {
            "home-welcome": {
              type: "text",
              title: "Welcome Home",
              value:
                "This is the home screen with a Users table example. The table below is a SuperAppObject loaded from the config collection.",
            },
            "home-users-table": {
              type: "superappobject",
              configObj: "Users",
              displayState: "TABLE",
              title: "Users Table",
            },
          },
        },
        products: {
          type: "layout",
          structure: {
            type: "row",
            sectionProps: {
              style: {
                display: "flex",
                flexDirection: "column",
                gap: "24px",
                padding: "24px",
              },
            },
            children: [
              {
                type: "block",
                block: "products-header",
                id: "products-header-block",
              },
              {
                type: "block",
                block: "products-table",
                id: "products-table-block",
              },
            ],
          },
          blocks: {
            "products-header": {
              type: "text",
              title: "Products Catalog",
              value:
                "This page demonstrates the datagrid/table configuration feature. All table columns, sorting, and display options are configured in the database using the Table/Column Editor (section 7) - no code changes needed! The table below shows a SuperAppObject with database-driven column configuration.",
            },
            "products-table": {
              type: "superappobject",
              configObj: "Users",
              displayState: "TABLE",
            },
          },
        },
        docs: {
          type: "markdown",
          title: "Documentation",
          text: "## Getting Started\n\nWelcome to the SuperApp documentation.\n\n### Features\n\n- Dynamic layouts\n- SuperAppObjects\n- Low-code pages\n\n[Learn more](/docs/guides)",
        },
        "docs/guides": {
          type: "layout",
          structure: {
            type: "row",
            sectionProps: {
              style: {
                display: "flex",
                flexDirection: "column",
                gap: "24px",
                padding: "24px",
              },
            },
            children: [
              {
                type: "block",
                block: "guides-header",
                id: "guides-header-block",
              },
              {
                type: "block",
                block: "guides-content",
                id: "guides-content-block",
              },
            ],
          },
          blocks: {
            "guides-header": {
              type: "text",
              title: "Guides",
              value:
                "Welcome to the Guides section. Here you'll find step-by-step tutorials and guides for using the SuperApp platform.",
            },
            "guides-content": {
              type: "html",
              title: "Getting Started Guides",
              html: `
                <div style="display: flex; flex-direction: column; gap: 16px;">
                  <div style="padding: 16px; background: rgba(255,255,255,0.05); border-radius: 8px;">
                    <h3 style="margin-top: 0; color: #fff;">Quick Start Guide</h3>
                    <p style="color: #fff; opacity: 0.9;">Learn how to set up your first SuperApp configuration in minutes.</p>
                    <ul style="color: #fff; opacity: 0.9;">
                      <li>Configure your header and menu items</li>
                      <li>Set up feature flags</li>
                      <li>Create your first SuperAppObject</li>
                      <li>Build custom layouts</li>
                    </ul>
                  </div>
                  <div style="padding: 16px; background: rgba(255,255,255,0.05); border-radius: 8px;">
                    <h3 style="margin-top: 0; color: #fff;">Advanced Configuration</h3>
                    <p style="color: #fff; opacity: 0.9;">Dive deeper into advanced features and customization options.</p>
                    <ul style="color: #fff; opacity: 0.9;">
                      <li>Custom field configurations</li>
                      <li>Table column customization</li>
                      <li>Function stringification</li>
                      <li>Multi-tenant support</li>
                    </ul>
                  </div>
                </div>
              `,
            },
          },
        },
        "docs/api": {
          type: "layout",
          structure: {
            type: "row",
            sectionProps: {
              style: {
                display: "flex",
                flexDirection: "column",
                gap: "24px",
                padding: "24px",
              },
            },
            children: [
              {
                type: "block",
                block: "api-header",
                id: "api-header-block",
              },
              {
                type: "block",
                block: "api-endpoints",
                id: "api-endpoints-block",
              },
            ],
          },
          blocks: {
            "api-header": {
              type: "text",
              title: "API Reference",
              value:
                "Welcome to the API Reference. Here you'll find documentation for all available API endpoints and methods.",
            },
            "api-endpoints": {
              type: "html",
              title: "Available Endpoints",
              html: `
                <div style="display: flex; flex-direction: column; gap: 16px;">
                  <div style="padding: 16px; background: rgba(255,255,255,0.05); border-radius: 8px;">
                    <h3 style="margin-top: 0; color: #fff;">GET /api/superapp/config/:appId</h3>
                    <p style="color: #fff; opacity: 0.9;">Retrieve the SuperApp configuration for a specific app ID.</p>
                    <code style="display: block; padding: 8px; background: rgba(0,0,0,0.3); border-radius: 4px; color: #4fc3f7; margin-top: 8px;">
                      GET /api/superapp/config/Ben-test
                    </code>
                  </div>
                  <div style="padding: 16px; background: rgba(255,255,255,0.05); border-radius: 8px;">
                    <h3 style="margin-top: 0; color: #fff;">PUT /api/superapp/config/:appId</h3>
                    <p style="color: #fff; opacity: 0.9;">Update the SuperApp configuration for a specific app ID.</p>
                    <code style="display: block; padding: 8px; background: rgba(0,0,0,0.3); border-radius: 4px; color: #4fc3f7; margin-top: 8px;">
                      PUT /api/superapp/config/Ben-test
                    </code>
                  </div>
                  <div style="padding: 16px; background: rgba(255,255,255,0.05); border-radius: 8px;">
                    <h3 style="margin-top: 0; color: #fff;">POST /api/saveObject/config</h3>
                    <p style="color: #fff; opacity: 0.9;">Save a ConfigObj to the database. Used for SuperAppObjects.</p>
                    <code style="display: block; padding: 8px; background: rgba(0,0,0,0.3); border-radius: 4px; color: #4fc3f7; margin-top: 8px;">
                      POST /api/saveObject/config
                    </code>
                  </div>
                </div>
              `,
            },
          },
        },
        about: {
          type: "layout",
          structure: {
            type: "row",
            sectionProps: {
              style: {
                display: "flex",
                flexDirection: "column",
                gap: "24px",
                padding: "24px",
              },
            },
            children: [
              {
                type: "block",
                block: "about-header",
                id: "about-header-block",
              },
              {
                type: "block",
                block: "about-content",
                id: "about-content-block",
              },
            ],
          },
          blocks: {
            "about-header": {
              type: "text",
              title: "About Us",
              value:
                "Learn more about our company, mission, and the SuperApp platform.",
            },
            "about-content": {
              type: "html",
              title: "Our Story",
              html: `
                <div style="display: flex; flex-direction: column; gap: 16px;">
                  <div style="padding: 16px; background: rgba(255,255,255,0.05); border-radius: 8px;">
                    <h3 style="margin-top: 0; color: #fff;">Our Mission</h3>
                    <p style="color: #fff; opacity: 0.9;">We're building the future of configurable applications. Our SuperApp platform allows you to create powerful, database-driven applications without writing code.</p>
                  </div>
                  <div style="padding: 16px; background: rgba(255,255,255,0.05); border-radius: 8px;">
                    <h3 style="margin-top: 0; color: #fff;">Key Features</h3>
                    <ul style="color: #fff; opacity: 0.9;">
                      <li><strong>Founded in 2024</strong> - A modern approach to application development</li>
                      <li><strong>Database-Driven</strong> - All configuration stored in Firestore</li>
                      <li><strong>Multi-Tenant</strong> - Support for multiple app instances</li>
                      <li><strong>Low-Code</strong> - Visual editors for layouts, menus, and objects</li>
                      <li><strong>Extensible</strong> - Create custom SuperAppObjects and configurations</li>
                    </ul>
                  </div>
                  <div style="padding: 16px; background: rgba(255,255,255,0.05); border-radius: 8px;">
                    <h3 style="margin-top: 0; color: #fff;">Technology Stack</h3>
                    <p style="color: #fff; opacity: 0.9;">Built with React, Firestore, and modern web technologies. Designed for scalability and flexibility.</p>
                  </div>
                </div>
              `,
            },
          },
        },
      },
    };

    try {
      setSaving(true);
      // Include app_config in the SuperApp config (stored in superapp_configs)
      const demoConfigWithAppConfig = {
        ...demoConfig,
        app_config: demoAppConfig, // Store ConfigObjs in superapp_configs, not config collection
      };
      console.log("💾 Saving demo config with app_config:", {
        hasAppConfig: !!demoConfigWithAppConfig.app_config,
        appConfigKeys: demoConfigWithAppConfig.app_config
          ? Object.keys(demoConfigWithAppConfig.app_config)
          : [],
        sampleTableColumn:
          demoConfigWithAppConfig.app_config?.Users?.table?.[0],
      });
      await saveConfig(demoConfigWithAppConfig);
      await seedDemoConfigCollection(demoAppConfig);
      alert(
        "Demo config + sample ConfigObjs loaded! Refresh the page to see it in action."
      );
      window.location.reload();
    } catch (e) {
      console.error("Failed to load demo config:", e);
      alert(`Error: ${e.message || "Failed to load demo config"}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div
        className="page-editor-container"
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <div className="loading-spinner">Loading config...</div>
      </div>
    );
  }

  return (
    <div className="page-editor-container">
      <div className="page-editor-header">
        <div className="custom-container">
          <div className="d-flex justify-content-between align-items-center">
            <h1 className="gradient-text">SuperApp Config Editor</h1>
            <div className="d-flex gap-2">
              <SuperAppButton
                className="primary-gradient-btn"
                size="sm"
                onClick={() =>
                  window.open(`/?appId=${encodeURIComponent(appId)}`, "_blank")
                }
              >
                <FaEye /> View Site
              </SuperAppButton>
              <SuperAppButton
                className="secondary-btn"
                size="sm"
                onClick={loadDemoConfig}
              >
                Load Demo Config
              </SuperAppButton>
              {saving && <span className="saving-indicator">Saving...</span>}
            </div>
          </div>
        </div>
      </div>

      <div className="custom-container">
        <div className="editor-card mb-4">
          <div className="card-body">
            <label className="form-label">App ID</label>
            <input
              value={appId}
              onChange={(e) => {
                const newId = e.target.value;
                setAppId(newId);
                window.location.search = `?appId=${encodeURIComponent(newId)}`;
              }}
              className="premium-input"
            />
          </div>
        </div>

        {/* HEADER SECTION */}
        <div id="section-header" className="editor-card mb-4">
          <div
            className="card-header-flex cursor-pointer"
            onClick={() => toggleSection("header")}
          >
            <h3 className="card-title">
              <FaLayerGroup className="icon" /> 1. Header
            </h3>
            <span className="expand-icon">
              {expandedSections.header ? "▼" : "▶"}
            </span>
          </div>
          {expandedSections.header && (
            <div className="card-body">
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">Title</label>
                  <input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="My SuperApp"
                    className="premium-input"
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">Brand Logo URL</label>
                  <input
                    value={brandLogo}
                    onChange={(e) => setBrandLogo(e.target.value)}
                    placeholder="https://example.com/logo.png"
                    className="premium-input"
                  />
                </div>
              </div>
              <div className="mb-3">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={showLikes}
                    onChange={(e) => setShowLikes(e.target.checked)}
                  />
                  Show likes
                </label>
              </div>
              <button
                onClick={saveHeader}
                disabled={saving}
                className="primary-gradient-btn"
              >
                {saving ? "Saving…" : "Save Header"}
              </button>

              {/* Header Payload JSON Viewer */}
              <div className="mt-4 pt-3 border-top-subtle">
                <div
                  className="d-flex justify-content-between align-items-center cursor-pointer"
                  onClick={() =>
                    setExpandedSections((prev) => ({
                      ...prev,
                      headerJson: !prev.headerJson,
                    }))
                  }
                >
                  <label className="cursor-pointer mb-0 text-muted">
                    View Header Payload (JSON)
                  </label>
                  <span className="text-muted">
                    {expandedSections.headerJson ? "▼" : "▶"}
                  </span>
                </div>
                {expandedSections.headerJson && (
                  <pre className="code-block mt-2">
                    {JSON.stringify(
                      cfg?.header || {
                        title: title || "",
                        brand: { logoUrl: brandLogo || "" },
                        showLikes: showLikes,
                        primaryMenu: { items: menuItems || [] },
                      },
                      null,
                      2
                    )}
                  </pre>
                )}
              </div>
            </div>
          )}
        </div>

        {/* MENU SECTION */}
        <div id="section-menu" className="editor-card mb-4">
          <div
            className="card-header-flex cursor-pointer"
            onClick={() => toggleSection("menu")}
          >
            <h3 className="card-title">
              <FaList className="icon" /> 2. Menu Items
            </h3>
            <span className="expand-icon">
              {expandedSections.menu ? "▼" : "▶"}
            </span>
          </div>
          {expandedSections.menu && (
            <div className="card-body">
              <div className="sub-card mb-4">
                <h4 className="sub-card-title">
                  <FaPlus className="icon-sm" /> Add Menu Item
                </h4>
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Label</label>
                    <input
                      value={mLabel}
                      onChange={(e) => setMLabel(e.target.value)}
                      placeholder="e.g., Docs"
                      className="premium-input"
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Path or URL (optional)</label>
                    <input
                      value={mPathOrUrl}
                      onChange={(e) => setMPathOrUrl(e.target.value)}
                      placeholder="e.g., /docs or https://example.com"
                      className="premium-input"
                    />
                  </div>
                </div>
                <div className="row align-items-end">
                  <div className="col-md-3 mb-3">
                    <label className="form-label">Order</label>
                    <input
                      value={mOrder}
                      onChange={(e) => setMOrder(e.target.value)}
                      className="premium-input"
                    />
                  </div>
                  <div className="col-md-3 mb-3">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={mVisible}
                        onChange={(e) => setMVisible(e.target.checked)}
                      />
                      Visible
                    </label>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Parent (optional)</label>
                    <select
                      value={mParentKey}
                      onChange={(e) => setMParentKey(e.target.value)}
                      className="premium-input"
                    >
                      <option value="">(Top level)</option>
                      {collectParentOptions.map(({ key, label, depth }) => (
                        <option key={`${key}-${depth}`} value={key}>
                          {"".padStart(depth * 2, " ")} {label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <button
                  onClick={addMenuItem}
                  disabled={saving}
                  className="primary-gradient-btn"
                >
                  {saving ? "Saving…" : "Create Menu Item"}
                </button>
              </div>

              <div className="mt-4">
                <h4 className="sub-card-title">Current Menu Structure</h4>
                {menuItems.length === 0 ? (
                  <div className="text-muted">No menu items yet.</div>
                ) : (
                  <div className="tree-editor-wrapper">
                    <MenuTreeEditor
                      items={menuItems}
                      onChange={handleMenuStructureChange}
                      onRequestAddChild={(parentKey) =>
                        setMParentKey(parentKey)
                      }
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* FEATURES SECTION */}
        <div id="section-features" className="editor-card mb-4">
          <div
            className="card-header-flex cursor-pointer"
            onClick={() => toggleSection("features")}
          >
            <h3 className="card-title">
              <FaToggleOn className="icon" /> 3. Feature Flags
            </h3>
            <span className="expand-icon">
              {expandedSections.features ? "▼" : "▶"}
            </span>
          </div>
          {expandedSections.features && (
            <div className="card-body">
              <div className="mb-3">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={showLogin}
                    onChange={(e) => setShowLogin(e.target.checked)}
                  />
                  Show Login Button
                </label>
              </div>
              <div className="mb-3">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={showTryForFree}
                    onChange={(e) => setShowTryForFree(e.target.checked)}
                  />
                  Show Try for Free Button
                </label>
              </div>
              <button
                onClick={saveFeatures}
                disabled={saving}
                className="primary-gradient-btn"
              >
                {saving ? "Saving…" : "Save Features"}
              </button>
            </div>
          )}
        </div>

        {/* LAYOUT SECTION */}
        <div id="section-layout" className="editor-card mb-4">
          <div
            className="card-header-flex cursor-pointer"
            onClick={() => toggleSection("layout")}
          >
            <h3 className="card-title">
              <FaColumns className="icon" /> 4. Layout
            </h3>
            <span className="expand-icon">
              {expandedSections.layout ? "▼" : "▶"}
            </span>
          </div>
          {expandedSections.layout && (
            <div className="card-body">
              <div className="d-flex gap-2 mb-3">
                <button
                  onClick={() => setLayoutEditorMode("visual")}
                  className={`mode-btn ${
                    layoutEditorMode === "visual" ? "active" : ""
                  }`}
                >
                  Visual Editor
                </button>
                <button
                  onClick={() => setLayoutEditorMode("json")}
                  className={`mode-btn ${
                    layoutEditorMode === "json" ? "active" : ""
                  }`}
                >
                  JSON Editor
                </button>
              </div>

              {layoutEditorMode === "visual" ? (
                <LayoutVisualEditor
                  layoutJson={layoutJson}
                  setLayoutJson={setLayoutJson}
                  cfg={cfg}
                  editingTarget={editingTarget}
                  setEditingTarget={setEditingTarget}
                  // Vik (Jan 9, 2026) : Passed onLayoutChange callback to allow child to update parent state
                  onLayoutChange={handleLayoutChange}
                />
              ) : (
                <>
                  <label className="form-label">Layout JSON (editable)</label>
                  <textarea
                    value={layoutJson}
                    onChange={(e) => {
                      setLayoutJson(e.target.value);
                      setJsonErrors((prev) => ({ ...prev, layout: "" }));
                    }}
                    className="code-editor"
                    placeholder='{"route": "/", "structure": {...}}'
                  />
                  {jsonErrors.layout && (
                    <div className="error-text mt-2">{jsonErrors.layout}</div>
                  )}
                </>
              )}
              <button
                onClick={saveLayout}
                disabled={saving}
                className="primary-gradient-btn mt-3"
              >
                {saving ? "Saving…" : "Save Layout"}
              </button>
            </div>
          )}
        </div>

        {/* UNIFIED SUPERAPP OBJECT EDITOR */}
        <div id="section-configobj" className="editor-card mb-4">
          <div
            className="card-header-flex cursor-pointer"
            onClick={() => toggleSection("configobj")}
          >
            <h3 className="card-title">
              <FaDatabase className="icon" /> 5. SuperAppObject Editor (Unified)
            </h3>
            <span className="expand-icon">
              {expandedSections.configobj ? "▼" : "▶"}
            </span>
          </div>
          {expandedSections.configobj && (
            <div className="card-body">
              <p className="text-muted mb-3">
                Unified editor for SuperAppObjects. Create, edit fields,
                configure table columns, and view raw JSON all in one place.
              </p>
              <UnifiedSuperAppObjectEditor appId={appId} />
            </div>
          )}
        </div>

        {/* RAW JSON SECTION */}
        <div id="section-raw" className="editor-card mb-4">
          <div
            className="card-header-flex cursor-pointer"
            onClick={() => toggleSection("raw")}
          >
            <h3 className="card-title">
              <FaCode className="icon" /> 6. Raw JSON (Main SuperApp Config)
            </h3>
            <span className="expand-icon">
              {expandedSections.raw ? "▼" : "▶"}
            </span>
          </div>
          {expandedSections.raw && (
            <div className="card-body">
              <label className="form-label">Full Config JSON</label>
              <textarea
                value={rawJson}
                onChange={(e) => {
                  setRawJson(e.target.value);
                  setJsonErrors((prev) => ({ ...prev, raw: "" }));
                }}
                className="code-editor"
              />
              {jsonErrors.raw && (
                <div className="error-text mt-2">{jsonErrors.raw}</div>
              )}
              <button
                onClick={saveRawJson}
                disabled={saving}
                className="primary-gradient-btn mt-3"
              >
                {saving ? "Saving…" : "Save Full Config"}
              </button>
            </div>
          )}
        </div>
      </div>

      <style>{`
        .page-editor-container {
          min-height: 100vh;
          background: #0b1221;
          font-family: 'Inter', sans-serif;
          color: #fff;
          padding-bottom: 60px;
        }
        .page-editor-header {
          background: rgba(11, 18, 33, 0.95);
          padding: 20px 0;
          border-bottom: 1px solid rgba(255,255,255,0.05);
          margin-bottom: 30px;
          position: sticky;
          top: 0;
          z-index: 100;
          backdrop-filter: blur(10px);
        }
        .custom-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 20px;
        }
        .gradient-text {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          font-weight: 800;
          font-size: 2rem;
          margin: 0;
        }
        
        /* Cards */
        .editor-card {
          background: #1a2035;
          border-radius: 16px;
          border: 1px solid rgba(255,255,255,0.05);
          box-shadow: 0 4px 20px rgba(0,0,0,0.2);
          overflow: hidden;
        }
        .sub-card {
          background: rgba(255,255,255,0.03);
          border-radius: 12px;
          padding: 20px;
          border: 1px solid rgba(255,255,255,0.05);
        }
        .card-header-flex {
          padding: 20px 24px;
          border-bottom: 1px solid rgba(255,255,255,0.05);
          background: rgba(255,255,255,0.02);
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .card-title {
          margin: 0;
          font-size: 1.1rem;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 10px;
          color: #fff;
        }
        .sub-card-title {
          margin-top: 0;
          margin-bottom: 16px;
          font-size: 1rem;
          color: #fff;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .card-body {
          padding: 24px;
        }
        
        /* Inputs */
        .premium-input {
          width: 100%;
          padding: 12px 16px;
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 8px;
          color: #fff;
          font-size: 14px;
          transition: all 0.2s;
        }
        .premium-input:focus {
          outline: none;
          border-color: #764ba2;
          background: rgba(255,255,255,0.08);
          box-shadow: 0 0 0 3px rgba(118, 75, 162, 0.1);
        }
        .code-editor {
          width: 100%;
          min-height: 200px;
          padding: 12px;
          background: #090b10;
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 8px;
          color: #a9b7c6;
          font-family: 'JetBrains Mono', monospace;
          font-size: 13px;
          line-height: 1.5;
        }
        
        /* Buttons */
        .primary-gradient-btn {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border: none;
          padding: 10px 24px;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }
        .primary-gradient-btn:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(118, 75, 162, 0.3);
        }
        .primary-gradient-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          filter: grayscale(1);
        }
        .secondary-btn {
          background: rgba(255,255,255,0.1);
          color: #fff;
          border: 1px solid rgba(255,255,255,0.1);
          padding: 8px 16px;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s;
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }
        .secondary-btn:hover {
          background: rgba(255,255,255,0.15);
        }
        .mode-btn {
          padding: 8px 16px;
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.1);
          color: rgba(255,255,255,0.6);
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.2s;
        }
        .mode-btn.active {
          background: rgba(118, 75, 162, 0.2);
          border-color: #764ba2;
          color: #fff;
        }
        
        /* Helpers */
        .d-flex { display: flex; }
        .gap-2 { gap: 8px; }
        .justify-content-between { justify-content: space-between; }
        .align-items-center { align-items: center; }
        .align-items-end { align-items: flex-end; }
        .mb-3 { margin-bottom: 16px; }
        .mb-4 { margin-bottom: 24px; }
        .mt-2 { margin-top: 8px; }
        .mt-3 { margin-top: 16px; }
        .mt-4 { margin-top: 24px; }
        .pt-3 { padding-top: 16px; }
        .row { display: flex; flex-wrap: wrap; margin: 0 -10px; }
        .col-md-3 { width: 25%; padding: 0 10px; box-sizing: border-box; }
        .col-md-6 { width: 50%; padding: 0 10px; box-sizing: border-box; }
        .form-label { display: block; margin-bottom: 8px; font-weight: 500; font-size: 0.9rem; color: rgba(255,255,255,0.8); }
        .checkbox-label { display: flex; gap: 10px; align-items: center; color: #fff; cursor: pointer; }
        .cursor-pointer { cursor: pointer; }
        .text-muted { color: rgba(255,255,255,0.5); }
        .border-top-subtle { border-top: 1px solid rgba(255,255,255,0.1); }
        .code-block {
          background: rgba(0,0,0,0.3);
          padding: 16px;
          border-radius: 8px;
          overflow: auto;
          max-height: 400px;
          font-size: 12px;
          color: #a9b7c6;
          border: 1px solid rgba(255,255,255,0.1);
        }
        .icon { font-size: 1.1em; }
        .icon-sm { font-size: 0.9em; }
        .expand-icon { color: rgba(255,255,255,0.5); }
        .error-text { color: #ff6b6b; font-size: 0.85rem; }
        .tree-editor-wrapper {
          background: rgba(0,0,0,0.2);
          border-radius: 8px;
          padding: 16px;
          border: 1px solid rgba(255,255,255,0.05);
        }
        
        /* MenuTreeEditor Styles */
        .menu-tree-container {
          font-family: 'Inter', sans-serif;
        }
        .menu-tree-node {
          display: flex;
          align-items: center;
          padding: 8px 0;
          border-bottom: 1px solid rgba(255,255,255,0.05);
          transition: background 0.2s;
        }
        .menu-tree-node:hover {
          background: rgba(255,255,255,0.02);
        }
        .menu-tree-node-disabled {
          opacity: 0.5;
        }
        .menu-tree-drag {
          cursor: grab;
          color: rgba(255,255,255,0.4);
          margin-right: 8px;
          display: flex;
          align-items: center;
        }
        .menu-tree-drag:active {
          cursor: grabbing;
          color: #fff;
        }
        .menu-tree-toggle {
          background: none;
          border: none;
          color: rgba(255,255,255,0.6);
          cursor: pointer;
          display: flex;
          align-items: center;
          padding: 4px;
          margin-right: 4px;
        }
        .menu-tree-toggle:hover {
          color: #fff;
        }
        .menu-tree-label {
          flex: 1;
          color: #fff;
          font-size: 14px;
          font-weight: 500;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          /* Vik (Jan 9, 2026) : Added margin to separate label from toggle button */
          margin-right: 16px;
        }
        .menu-tree-badge {
          background: #764ba2;
          color: #fff;
          font-size: 10px;
          padding: 2px 6px;
          border-radius: 4px;
          margin-left: 8px;
          font-weight: 600;
        }
        .menu-tree-disabled {
          background: rgba(255,255,255,0.1);
          color: rgba(255,255,255,0.6);
          font-size: 10px;
          padding: 2px 6px;
          border-radius: 4px;
          margin-left: 8px;
        }
        .menu-tree-spacer {
          /* Vik (Jan 9, 2026) : Hidden spacer to allow label to take full width and prevent premature truncation */
          display: none;
        }
        .menu-tree-btn {
          background: none;
          border: none;
          color: rgba(255,255,255,0.4);
          cursor: pointer;
          padding: 6px;
          display: flex;
          align-items: center;
          transition: color 0.2s;
        }
        .menu-tree-btn:hover {
          color: #fff;
        }
        .menu-tree-dragging {
          opacity: 0.5;
          background: rgba(118, 75, 162, 0.1);
        }
        .menu-tree-drop-target {
          background: rgba(118, 75, 162, 0.1);
          border-radius: 4px;
        }
      `}</style>
    </div>
  );
}

/**
 * Visual Accordion Editor for Layout Blocks
 * Shows blocks in an accordion format for easier editing
 */
/**
 * Visual Accordion Editor for Layout Blocks
 * Shows blocks in an accordion format for easier editing
 */
function LayoutVisualEditor({
  layoutJson,
  setLayoutJson,
  cfg,
  editingTarget,
  setEditingTarget,
  onLayoutChange,
}) {
  const [selectedLayoutTemplate, setSelectedLayoutTemplate] = useState("");
  const [expandedBlocks, setExpandedBlocks] = useState({});
  const [showNewBlockForm, setShowNewBlockForm] = useState(false);
  const [newBlockKey, setNewBlockKey] = useState("");
  const [newBlockData, setNewBlockData] = useState({ type: "text" });

  // Determine current layout based on target
  const currentLayout = useMemo(() => {
    if (editingTarget === "default") {
      try {
        return layoutJson ? JSON.parse(layoutJson) : cfg?.layout || {};
      } catch (e) {
        return {};
      }
    } else {
      return cfg?.screens?.[editingTarget]?.layout || {};
    }
  }, [editingTarget, layoutJson, cfg]);

  const blocks = currentLayout?.blocks || {};
  const blockKeys = Object.keys(blocks);

  // Update layout when target changes
  useEffect(() => {
    if (editingTarget === "default") {
      // Reset to default layout from props/cfg
      // layoutJson is already managed by parent for default layout
    } else {
      // For pages, we don't use layoutJson prop directly, we update cfg directly via a helper
      // But here we just need to ensure the UI reflects the page's layout
    }
  }, [editingTarget]);

  const updateLayout = (newLayout) => {
    if (onLayoutChange) {
      // Vik (Jan 9, 2026) : Use callback to update parent state directly
      onLayoutChange(editingTarget, newLayout);
    } else if (editingTarget === "default") {
      setLayoutJson(JSON.stringify(newLayout, null, 2));
    } else {
      // Update specific page layout in cfg
      // We need to trigger a save or update the parent's cfg state
      // Since we can't easily update parent cfg from here without a callback,
      // we'll use a hack: update the layoutJson if it's default,
      // but for pages we might need to inject a callback or use a different approach.
      //
      // BETTER APPROACH: The parent component should handle the save.
      // But for now, let's assume we can only update layoutJson if we are in default mode.
      // If we are in page mode, we need to update the cfg object directly.

      // Actually, let's modify the parent to handle this, but since we are replacing this component,
      // let's try to update the cfg directly if possible, or warn.
      //
      // Wait, the parent passes `cfg` but not `setCfg`.
      // We need `setCfg` or a way to update it.
      // Let's assume for this step we will just update the visual state and let the user click "Save Layout".
      // But "Save Layout" in parent only saves `layoutJson` to `cfg.layout`.
      // It doesn't save to `cfg.screens`.

      // CRITICAL: We need to update the parent's `saveLayout` function to handle pages too.
      // For now, let's implement the UI and then we'll fix the parent's save logic in the next step.
      // We will emit a custom event or just modify the object reference (risky but works in React for deep updates sometimes if forced).

      // Actually, let's just update the local `currentLayout` and we'll need to push it back.
      // Since we can't push back easily without `setCfg`, we will modify the `cfg` object in place
      // and force a re-render if possible, or better, we should have asked to pass `setCfg`.

      // Let's use a temporary solution: We will assume the parent will be updated to handle page saving.
      // For now, we will just log the update.
      console.warn("Page layout update not fully wired yet without setCfg");
    }
  };

  // Helper to apply layout template
  const applyLayoutTemplate = (templateValue) => {
    const template = LAYOUT_OPTIONS.find((opt) => opt.value === templateValue);
    if (!template) return;

    const newStructure = JSON.parse(JSON.stringify(template.structure));
    const newLayout = { ...currentLayout, structure: newStructure };

    // Distribute existing blocks if any
    if (blockKeys.length > 0 && newStructure.children) {
      blockKeys.forEach((key, idx) => {
        const colIdx = idx % newStructure.children.length;
        if (!newStructure.children[colIdx].children)
          newStructure.children[colIdx].children = [];
        newStructure.children[colIdx].children.push({
          type: "block",
          block: key,
        });
      });
    } else if (blockKeys.length > 0 && newStructure.tabs) {
      // Distribute to tabs
      blockKeys.forEach((key, idx) => {
        const tabIdx = idx % newStructure.tabs.length;
        newStructure.tabs[tabIdx].block = key;
      });
    }

    updateLayout(newLayout);
    setSelectedLayoutTemplate(templateValue);
  };

  const toggleBlock = (key) => {
    setExpandedBlocks((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const updateBlock = (key, updatedBlock) => {
    const newBlocks = { ...blocks, [key]: updatedBlock };
    const newLayout = { ...currentLayout, blocks: newBlocks };
    updateLayout(newLayout);
  };

  const deleteBlock = (key) => {
    if (!window.confirm(`Delete block "${key}"?`)) return;
    const newBlocks = { ...blocks };
    delete newBlocks[key];
    const newLayout = { ...currentLayout, blocks: newBlocks };
    updateLayout(newLayout);
  };

  const addBlock = () => {
    const key = newBlockKey.trim();
    if (!key) {
      alert("Please enter a block key");
      return;
    }
    if (blocks[key]) {
      alert(`Block "${key}" already exists.`);
      return;
    }
    const newBlocks = { ...blocks, [key]: newBlockData };
    const newLayout = { ...currentLayout, blocks: newBlocks };

    // Auto-add to structure
    if (newLayout.structure) {
      if (
        newLayout.structure.children &&
        newLayout.structure.children.length > 0
      ) {
        // Add to first column
        if (!newLayout.structure.children[0].children)
          newLayout.structure.children[0].children = [];
        newLayout.structure.children[0].children.push({
          type: "block",
          block: key,
        });
      } else if (newLayout.structure.tabs) {
        // Add to first empty tab or first tab
        const emptyTab = newLayout.structure.tabs.find((t) => !t.block);
        if (emptyTab) emptyTab.block = key;
      }
    }

    updateLayout(newLayout);
    setNewBlockKey("");
    setNewBlockData({ type: "text" });
    setShowNewBlockForm(false);
    setExpandedBlocks((prev) => ({ ...prev, [key]: true }));
  };

  const availablePages = cfg?.screens ? Object.keys(cfg.screens) : [];

  return (
    <div>
      <div
        style={{
          marginBottom: 16,
          padding: 12,
          background: "rgba(255,255,255,0.05)",
          borderRadius: 8,
        }}
      >
        <label style={{ color: "var(--text-color)", marginRight: 10 }}>
          Editing Target:
        </label>
        <select
          value={editingTarget}
          onChange={(e) => setEditingTarget(e.target.value)}
          style={{
            padding: 6,
            borderRadius: 4,
            background: "#1d1769",
            color: "var(--text-color)",
            border: "1px solid rgba(255,255,255,0.2)",
          }}
        >
          <option value="default">
            Default Layout (Route: {cfg?.layout?.route || "/"})
          </option>
          {availablePages.map((page) => (
            <option key={page} value={page}>
              Page: {page}
            </option>
          ))}
        </select>
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={{ color: "var(--text-color)", marginRight: 10 }}>
          Apply Template:
        </label>
        <select
          value={selectedLayoutTemplate}
          onChange={(e) => applyLayoutTemplate(e.target.value)}
          style={{
            padding: 6,
            borderRadius: 4,
            background: "#1d1769",
            color: "var(--text-color)",
            border: "1px solid rgba(255,255,255,0.2)",
          }}
        >
          <option value="">-- Select Layout --</option>
          {LAYOUT_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      <p style={{ color: "var(--text-color)", marginBottom: 16, fontSize: 14 }}>
        Edit layout blocks visually. Blocks are used in the layout structure.
      </p>

      {blockKeys.length === 0 ? (
        <p
          style={{
            color: "var(--text-color)",
            opacity: 0.7,
            fontStyle: "italic",
          }}
        >
          No blocks found. Add blocks below.
        </p>
      ) : (
        blockKeys.map((key) => (
          <div
            key={key}
            style={{
              marginBottom: 8,
              border: "1px solid rgba(255,255,255,0.2)",
              borderRadius: 4,
              overflow: "hidden",
            }}
          >
            <div
              style={{
                padding: 12,
                background: "rgba(255,255,255,0.05)",
                cursor: "pointer",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
              onClick={() => toggleBlock(key)}
            >
              <strong style={{ color: "#fff" }}>
                {key} ({blocks[key]?.type || "unknown"})
              </strong>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <span style={{ color: "#fff" }}>
                  {expandedBlocks[key] ? "▼" : "▶"}
                </span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteBlock(key);
                  }}
                  className="btn btn-sm btn-danger"
                  style={{ fontSize: 11, padding: "4px 8px" }}
                >
                  Delete
                </button>
              </div>
            </div>
            {expandedBlocks[key] && (
              <div
                style={{ padding: 12, background: "rgba(255,255,255,0.02)" }}
              >
                <BlockEditor
                  block={blocks[key]}
                  onUpdate={(updated) => updateBlock(key, updated)}
                />
              </div>
            )}
          </div>
        ))
      )}

      <div
        style={{
          marginTop: 16,
          padding: 12,
          border: "1px dashed rgba(255,255,255,0.3)",
          borderRadius: 4,
          background: "rgba(255,255,255,0.03)",
        }}
      >
        {!showNewBlockForm ? (
          <button
            type="button"
            className="btn btn-sm btn-primary"
            onClick={() => setShowNewBlockForm(true)}
          >
            + Add Block
          </button>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: 4,
                  color: "var(--text-color)",
                  fontSize: 12,
                }}
              >
                Block Key
              </label>
              <input
                value={newBlockKey}
                onChange={(e) => setNewBlockKey(e.target.value)}
                placeholder="e.g., hero, cards, cta"
                style={{
                  width: "100%",
                  padding: 6,
                  background: "var(--surface-color)",
                  border: "1px solid rgba(255,255,255,0.2)",
                  borderRadius: 4,
                  color: "var(--text-color)",
                  fontSize: 12,
                }}
              />
            </div>
            <BlockEditor block={newBlockData} onUpdate={setNewBlockData} />
            <div style={{ display: "flex", gap: 8 }}>
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={addBlock}
              >
                Save Block
              </button>
              <button
                type="button"
                className="btn btn-sm btn-secondary"
                onClick={() => setShowNewBlockForm(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Block Editor - Edit individual block properties
 */
function BlockEditor({ block, onUpdate }) {
  const [form, setForm] = useState(block || { type: "text" });

  useEffect(() => {
    setForm(block || { type: "text" });
  }, [block]);

  const handleChange = (field, value) => {
    const updated = { ...form, [field]: value };
    setForm(updated);
    onUpdate(updated);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div>
        <label
          style={{
            display: "block",
            marginBottom: 4,
            color: "var(--text-color)",
            fontSize: 12,
          }}
        >
          Type
        </label>
        <select
          value={form.type || "text"}
          onChange={(e) => handleChange("type", e.target.value)}
          style={{
            width: "100%",
            padding: 6,
            background: "var(--surface-color)",
            border: "1px solid rgba(255,255,255,0.2)",
            borderRadius: 4,
            color: "var(--text-color)",
            fontSize: 12,
          }}
        >
          <option value="text" style={{ background: "#1d1769", color: "#fff" }}>
            Text
          </option>
          <option value="hero" style={{ background: "#1d1769", color: "#fff" }}>
            Hero
          </option>
          <option
            value="cards"
            style={{ background: "#1d1769", color: "#fff" }}
          >
            Cards
          </option>
          <option value="cta" style={{ background: "#1d1769", color: "#fff" }}>
            CTA
          </option>
          <option
            value="superappobject"
            style={{ background: "#1d1769", color: "#fff" }}
          >
            SuperAppObject
          </option>
        </select>
      </div>
      {form.type === "text" && (
        <>
          <div>
            <label
              style={{
                display: "block",
                marginBottom: 4,
                color: "var(--text-color)",
                fontSize: 12,
              }}
            >
              Title
            </label>
            <input
              type="text"
              value={form.title || ""}
              onChange={(e) => handleChange("title", e.target.value)}
              style={{
                width: "100%",
                padding: 6,
                background: "var(--surface-color)",
                border: "1px solid rgba(255,255,255,0.2)",
                borderRadius: 4,
                color: "var(--text-color)",
                fontSize: 12,
              }}
            />
          </div>
          <div>
            <label
              style={{
                display: "block",
                marginBottom: 4,
                color: "var(--text-color)",
                fontSize: 12,
              }}
            >
              Value
            </label>
            <textarea
              value={form.value || ""}
              onChange={(e) => handleChange("value", e.target.value)}
              style={{
                width: "100%",
                padding: 6,
                background: "var(--surface-color)",
                border: "1px solid rgba(255,255,255,0.2)",
                borderRadius: 4,
                color: "var(--text-color)",
                fontSize: 12,
                minHeight: 60,
              }}
            />
          </div>
        </>
      )}
      {form.type === "hero" && (
        <>
          <div>
            <label
              style={{
                display: "block",
                marginBottom: 4,
                color: "var(--text-color)",
                fontSize: 12,
              }}
            >
              Title
            </label>
            <input
              type="text"
              value={form.title || ""}
              onChange={(e) => handleChange("title", e.target.value)}
              style={{
                width: "100%",
                padding: 6,
                background: "var(--surface-color)",
                border: "1px solid rgba(255,255,255,0.2)",
                borderRadius: 4,
                color: "var(--text-color)",
                fontSize: 12,
              }}
            />
          </div>
          <div>
            <label
              // Vik (Jan 14, 2026) : Inline styles updated to use CSS variables for theme consistency.
              style={{
                display: "block",
                marginBottom: 4,
                color: "var(--text-color)",
                fontSize: 12,
              }}
            >
              Subtitle
            </label>
            <input
              type="text"
              value={form.subtitle || ""}
              onChange={(e) => handleChange("subtitle", e.target.value)}
              style={{
                width: "100%",
                padding: 6,
                background: "var(--surface-color)",
                border: "1px solid rgba(255,255,255,0.2)",
                borderRadius: 4,
                color: "var(--text-color)",
                fontSize: 12,
              }}
            />
          </div>
        </>
      )}
      {form.type === "superappobject" && (
        <>
          <div>
            <label
              style={{
                display: "block",
                marginBottom: 4,
                color: "var(--text-color)",
                fontSize: 12,
              }}
            >
              ConfigObj Name
            </label>
            <input
              type="text"
              value={form.configObj || ""}
              onChange={(e) => handleChange("configObj", e.target.value)}
              placeholder="e.g., Users"
              style={{
                width: "100%",
                padding: 6,
                background: "var(--surface-color)",
                border: "1px solid rgba(255,255,255,0.2)",
                borderRadius: 4,
                color: "var(--text-color)",
                fontSize: 12,
              }}
            />
          </div>
        </>
      )}
    </div>
  );
}
